/**
 * useOfflineSalvageReport — forwards boot recovery's mid-capture salvage tally
 * to telemetry, once, after the WebView is up.
 *
 * ## Why a pull at mount and not a log line in Rust
 *
 * Boot recovery salvages partial WAVs left by a crash mid-consult and then
 * wipes `temp/`, so a take that failed salvage for a condition that would have
 * cleared on the next boot has its plaintext deleted anyway. Rust already
 * reports every one of those failures with a cause and a disposition — and in a
 * release build nobody hears it. Recovery runs inside the Tauri `setup` hook;
 * the log plugin's only release target is the WebView; the JS end of that
 * bridge is `attachTauriRustLogsToLogger`, called from an effect in `_app.tsx`;
 * and Tauri's emit has no replay. The lines are written into a bridge with
 * nothing on the far end.
 *
 * So Rust persists the counts to its own SQLite instead, and this reads them
 * back from the one position that can hear anything: after mount.
 *
 * ## Shape
 *
 * - **Once**, not a poll. The Rust read marks each run reported in the same
 *   transaction, so a second call can only return nothing.
 * - **Off the first-paint path.** The effect fires after commit and nothing
 *   renders from its result — this hook returns nothing and holds no state.
 * - **Never fatal.** A rejected read is warned about and swallowed. Warned
 *   about rather than swallowed silently, because a read that always throws
 *   looks from a dashboard exactly like a fleet with no salvage failures, which
 *   is the false zero this whole path exists to remove.
 * - **Not flag-gated.** It is headless, renders nothing, and self-guards on
 *   `isTauri()` — the same contract as the other offline-recording providers.
 *   Gating it would also hide the tally for exactly the boots that produced it.
 *
 * No PHI crosses this hook. The payload is counts plus an opaque per-boot id.
 */
import { useEffect, useRef } from 'react';

import {
  offlineRecordingBridge,
  type OfflineSalvageRunReport,
} from '@/tauri/offline-recording';
import { isTauri } from '@/tauri/utils';
import { logger } from '@/utils/logger/logger';

/**
 * Flatten the per-cause counts into one queryable field.
 *
 * A single string rather than an array or an object-per-cause because the
 * destination is a log attribute: an array of objects arrives at Datadog as an
 * unfacetable blob, while `key=n,key=n` is greppable, and the disposition
 * rollups beside it are already the facets worth alerting on. Empty for a run
 * that saw takes and failed none, which is a meaningful value and not a gap.
 */
function summariseCauses(run: OfflineSalvageRunReport): string {
  return run.causes.map(({ cause, count }) => `${cause}=${count}`).join(',');
}

function countByDisposition(run: OfflineSalvageRunReport, disposition: string): number {
  return run.causes
    .filter((cause) => cause.disposition === disposition)
    .reduce((total, cause) => total + cause.count, 0);
}

export function useOfflineSalvageReport(): void {
  // Guards the read against StrictMode's double-invoked effect and against any
  // later remount of the app root. The Rust side is already at-most-once, so
  // this is not what makes the delivery safe — it is what keeps a remount from
  // spending an IPC round trip to be told there is nothing to report.
  const hasRead = useRef(false);

  useEffect(() => {
    if (!isTauri() || hasRead.current) return;
    hasRead.current = true;

    void offlineRecordingBridge
      .takeSalvageReport()
      .then((runs) => {
        runs.forEach((run) => {
          // **Child bindings, never a payload object.** `logger.info(obj)`
          // `JSON.stringify`s `obj` into Datadog's `message` and forwards only
          // `child()` bindings as attributes — so a payload-object call would
          // land the whole tally as one unfacetable blob and `@transient` would
          // return nothing. That is precisely the "countable by eye, not by
          // facet" state the Rust taxonomy was built to leave behind, so
          // reproducing it here would waste the entire change. Same shape as
          // `withEnvelopeFields` in `src/tauri/tauriRustLogs.ts`.
          logger
            .child({
              // `tag` is what makes this a Datadog facet rather than free text,
              // and it deliberately matches the family the Rust lines already
              // use so one dashboard reads both.
              tag: 'offline_salvage_report',
              module: 'offline_recording',
              recovery_run: run.recoveryRun,
              occurred_at: run.occurredAt,
              takes_seen: run.takesSeen,
              salvaged: run.salvaged,
              transient: countByDisposition(run, 'transient'),
              terminal: countByDisposition(run, 'terminal'),
              benign: countByDisposition(run, 'benign'),
              causes: summariseCauses(run),
            })
            .info('Boot recovery mid-capture salvage tally');
        });
      })
      .catch((error: unknown) => {
        // **A classification, never the error itself.** `logger.warn(msg, err)`
        // packages the second argument as `additionalArgs` and the Datadog
        // transport ships it verbatim, so a rejected `invoke` would carry the
        // Rust `Display` text -- `Database error: {rusqlite}` / `IO error:
        // {std::io}` -- straight into telemetry. The IPC boundary already
        // scrubs absolute paths out of that string, but the rule in
        // `.ai-context/telemetry-phi-pii-safety.md` is that a caught error is
        // never handed to a telemetry call at all: the text is unbounded and
        // the scrubber only covers the one shape it knows. The tag is what makes
        // the failure countable, which is the whole reason this is warned about
        // rather than swallowed.
        logger.warn('[useOfflineSalvageReport] could not read the salvage tally', {
          error_kind: error instanceof Error ? error.name : typeof error,
        });
      });
  }, []);
}
